import { Injectable } from '@angular/core';
import { AclAssetPermission } from '@y42/acl/models';
import {
  DatasourceCTypeModel,
  DatasourceQuery,
  DatasourceStoreModel,
  DatasourceTypeQuery,
  DatasourceTypesContentfulService,
  UNKNOWN_DATASOURCE_TYPE_MODEL,
} from '@y42/datasource';
import { Y42FeatureToAclAssetResourceType } from '@y42/feature';
import { FeatureAclQuery } from '@y42/feature/acl';
import { GitQuery } from '@y42/git';
import { DatasourceTypeEnum, Y42FeatureType } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import { isNotNullish } from '@y42/util/nullish';
import { combineLatest, distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';
import { IntegrationTableQuery } from '../datasource-table/integration-table.query';
import { IntegrationGitService } from './integration-git.service';
import { IntegrationSettingsGitModel, IntegrationState, IntegrationUIModel } from './integration.model';
import { IntegrationStore } from './integration.store';

@Injectable({ providedIn: 'root' })
export class IntegrationQuery extends DatasourceQuery<IntegrationState, IntegrationUIModel> {
  constructor(
    protected readonly store: IntegrationStore,
    protected readonly datasourceTypeQuery: DatasourceTypeQuery,
    protected readonly datasourceTableQuery: IntegrationTableQuery,
    private readonly featureAclQuery: FeatureAclQuery,
    private readonly gitQuery: GitQuery,
    private readonly integrationGit: IntegrationGitService,
    private readonly datasourceTypesContentfulService: DatasourceTypesContentfulService,
  ) {
    super(Y42FeatureType.Integration, store, datasourceTypeQuery, datasourceTableQuery, {
      toUIModel: (storeModel) => {
        const { last_used, starred } = this.getValue();
        // Don't remove optional chaining operator, in some very rare cases settings can be undefined
        const $schema = (storeModel.settings as IntegrationSettingsGitModel | undefined)?.$schema ?? '';
        const typeModel = this.datasourceTypeQuery.findModelBySchema($schema) ?? UNKNOWN_DATASOURCE_TYPE_MODEL;

        const resourceType = Y42FeatureToAclAssetResourceType[this.featureType];
        assertNonNullable(resourceType);

        const integrationHasReadPermission = !!this.featureAclQuery.getPermission({
          resourceType,
          assetPath: storeModel.$path,
          permission: AclAssetPermission.Read,
        })?.allowed;

        const tables = storeModel.$children
          .map((id) => this.datasourceTableQuery.getUIEntity(id))
          .filter(isNotNullish)
          .map((table) => ({
            ...table,
            hasReadPermission: integrationHasReadPermission,
          }));

        return {
          ...storeModel,
          featureType: this.featureType,
          typeIcon: typeModel.logo,
          typeLabel: typeModel.displayName,
          children: tables,
          tables: tables,
          typeModel,
          last_used: last_used[storeModel.$path] || null,
          starred: !!starred[storeModel.$path],
          hasReadPermission: integrationHasReadPermission,
        };
      },
    });
  }

  readonly types$: Observable<DatasourceCTypeModel[] | null> = this.datasourceTypesContentfulService.data$.pipe(
    map((items) => {
      return (items ?? []).filter(
        (item) => ![DatasourceTypeEnum.UiModel, DatasourceTypeEnum.SqlModel].includes(item.type),
      );
    }),
  );

  readonly active$ = super.getActive$();

  readonly isActiveAuthorized$ = this.active$.pipe(
    switchMap((active) => (active ? this.selectIsAuthorized(active.$path) : of(false))),
    distinctUntilChanged(),
  );

  selectIsAuthorized($path: DatasourceStoreModel['$path']): Observable<boolean> {
    return combineLatest([this.gitQuery.fileTree$, this.selectEntity($path)]).pipe(
      map(() => this.integrationGit.checkIfFileExists(`${$path}/.y42/secret.enc`)),
    );
  }
}
